import { useMemo, useState } from 'react';
import { plotLabel as namePlot } from '@/lib/plotLabel';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import {
  LiveScreen, QueryState, Select, StatusPill, formatCurrency, formatCurrencyWhole, formatDate,
  type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { plotOptions } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';
import { printHtml } from '@/lib/consentDocument';
import { toast } from '@/lib/toast';

interface EntryRow {
  id: string;
  entry_code: string | null;
  entry_date: string;
  entry_type: string;
  category_label: string | null;
  description: string | null;
  amount: number;
  plot_code: string | null;
  approval_status: string;
}

interface Statement {
  opening_balance: number;
  income_total: number;
  expense_total: number;
  net_total: number;
  closing_balance: number;
  entry_count: number;
  period_start: string;
  period_end: string;
  includes_draft: boolean;
}

interface FarmerRow { id: string; party_id: string | null; farmer_code: string; full_name: string }

/** The first of a month a year back, and today: a period somebody can narrow rather than build. */
function defaultPeriod(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

/**
 * What a farmer earned and spent over a period, and what they are owed at the end of it.
 *
 * THE BALANCE IS THE POINT (owner, 2026-09-25: "shall need to have opening and closing balance").
 * A period's totals on their own say what moved, not what is owed: a farmer owed money in March is
 * still owed it in April. `api.fn_farmer_statement` answers both - everything before the period as
 * the opening balance, and the closing balance that follows - and takes the organisation from the
 * session, so no caller can total another organisation's money.
 *
 * The entries are READ-ONLY here. A line belongs to the harvest, sale or payment that created it;
 * a statement that could be edited in place would stop being a record of anything.
 */
export default function FarmerStatementScreen() {
  const period = useMemo(defaultPeriod, []);
  const [farmerId, setFarmerId] = useState('');
  // One plot of the farmer, or all of them (owner, 2026-09-27: "selection of farmer plot (not
  // farmer), even in P&L sheet also?"). fn_farmer_statement already takes the plot.
  const [plotId, setPlotId] = useState('');
  const [from, setFrom] = useState(period.from);
  const [to, setTo] = useState(period.to);
  const [stat, setStat] = useState<string | null>(null);

  const farmers = useRows<FarmerRow>('farmers', { orderBy: { column: 'full_name' } });
  const options = useMemo(
    () => (farmers.data ?? [])
      .filter((row) => row.party_id)
      .map((row) => ({ id: row.party_id!, label: `${row.full_name} (${row.farmer_code})` })),
    [farmers.data],
  );

  const ready = Boolean(farmerId && from && to && to >= from);
  const chosenFarmer = (farmers.data ?? []).find((row) => row.party_id === farmerId);
  // Every plot, for choosing a plot straight away: the farmer follows the plot.
  const allPlots = useQuery({ queryKey: ['picker', 'plots', 'with-farmer'], queryFn: () => plotOptions(), enabled: isSupabaseConfigured && !farmerId });
  function pickPlot(value: string) {
    if (!farmerId) {
      const plot = (allPlots.data ?? []).find((row) => row.id === value);
      const owner = (farmers.data ?? []).find((row) => row.id === plot?.farmer_id);
      if (owner?.party_id) setFarmerId(owner.party_id);
    }
    setPlotId(value);
    setStat(null);
  }
  const farmerPlots = useRows<{ id: string; plot_code: string; plot_no: string | null; plot_name: string | null }>('farm_plots', {
    filters: { farmer_id: chosenFarmer?.id }, orderBy: { column: 'plot_no' },
  }, Boolean(chosenFarmer?.id));

  const entries = useRows<EntryRow>('farmer_financial_entries', {
    filters: { farmer_party_id: farmerId, plot_id: plotId || undefined },
    orderBy: { column: 'entry_date', ascending: false },
    limit: 500,
  }, ready);

  const statement = useQuery({
    queryKey: ['farmer-statement', farmerId, plotId, from, to],
    queryFn: async () => {
      const rows = await callRpc<Statement[]>('fn_farmer_statement', {
        p_farmer_party_id: farmerId, p_period_start: from, p_period_end: to, p_plot_id: plotId || null,
      });
      return rows?.[0] ?? null;
    },
    enabled: isSupabaseConfigured && ready,
  });

  // The grid stands on exactly the entries the totals counted. The statement excludes drafts
  // unless the organisation says otherwise, and the function reports which rule it used - the
  // first version of this screen applied its own and showed an expense of 16,500 beside a total
  // of 12,500, which is the kind of disagreement that makes a reader distrust both numbers.
  const inPeriod = useMemo(() => {
    const window = (entries.data ?? []).filter((row) => row.entry_date >= from && row.entry_date <= to);
    if (statement.data?.includes_draft) return window;
    return window.filter((row) => row.approval_status === 'approved');
  }, [entries.data, from, to, statement.data]);

  /** In the period but not in the totals: shown as a line, never silently dropped. */
  const notCounted = useMemo(() => {
    if (!statement.data || statement.data.includes_draft) return [];
    return (entries.data ?? []).filter((row) =>
      row.entry_date >= from && row.entry_date <= to && row.approval_status !== 'approved');
  }, [entries.data, from, to, statement.data]);

  const STATS = useMemo(() => {
    const total = statement.data;
    return [
      {
        key: 'all', label: 'Entries', tone: 'neutral' as const, match: () => true,
        hint: total ? `Opening ${formatCurrencyWhole(total.opening_balance)}` : undefined,
      },
      { key: 'income', label: 'Income', tone: 'ok' as const, match: (row: EntryRow) => row.entry_type === 'income' },
      { key: 'expense', label: 'Expense', tone: 'warn' as const, match: (row: EntryRow) => row.entry_type === 'expense' },
    ];
  }, [statement.data]);

  const columns: Array<Column<EntryRow>> = [
    { key: 'entry_date', label: 'Date', render: (row) => formatDate(row.entry_date) },
    { key: 'entry_code', label: 'Entry', render: (row) => row.entry_code ?? '--' },
    { key: 'category_label', label: 'Category', render: (row) => row.category_label ?? '--' },
    // Plain words. This was a StatusPill fed the entry type, so every expense was labelled
    // "Pending" - a word that means something else entirely on a screen about money.
    { key: 'entry_type', label: 'Type', render: (row) => (row.entry_type === 'income' ? 'Income' : 'Expense') },
    { key: 'approval_status', label: 'Status', render: (row) => <StatusPill value={row.approval_status} /> },
    { key: 'plot_code', label: 'Plot', render: (row) => row.plot_code ?? '--' },
    { key: 'description', label: 'Note', render: (row) => row.description ?? '--' },
    // Money right-aligned and to the same number of decimals, so a column of it can be read down
    // and footed (owner, 2026-09-25: "re-align the columns, add the current symbol").
    { key: 'amount', label: 'Amount', align: 'right', render: (row) => formatCurrency(row.amount) },
  ];

  const shown = stat ? inPeriod.filter((row) => STATS.find((s) => s.key === stat)?.match(row as never)) : inPeriod;
  const farmerName = options.find((option) => option.id === farmerId)?.label ?? '';
  const chosenPlot = (farmerPlots.data ?? []).find((row) => row.id === plotId);
  const plotLabel = chosenPlot ? ` · plot ${[chosenPlot.plot_code, chosenPlot.plot_no].filter(Boolean).join(' · ')}` : '';

  function print() {
    const total = statement.data;
    if (!total) return;
    const rows = shown.map((row) => `<tr>
        <td>${formatDate(row.entry_date)}</td><td>${row.entry_code ?? ''}</td>
        <td>${row.category_label ?? ''}</td><td>${row.entry_type}</td>
        <td style="text-align:right">${formatCurrency(row.amount)}</td></tr>`).join('');
    const ok = printHtml(`<!doctype html><meta charset="utf-8"><title>P&amp;L sheet</title>
      <style>body{font:13px system-ui;padding:24px}table{border-collapse:collapse;width:100%}
      td,th{border-bottom:1px solid #ddd;padding:6px;text-align:left}
      .totals td{font-weight:600;border-top:2px solid #333}</style>
      <h2>P&amp;L sheet</h2>
      <p>${farmerName}${plotLabel}<br>${formatDate(total.period_start)} to ${formatDate(total.period_end)}</p>
      <table><tr><th>Date</th><th>Entry</th><th>Category</th><th>Type</th><th style="text-align:right">Amount</th></tr>
      ${rows}
      <tr class="totals"><td colspan="4">Opening balance</td><td style="text-align:right">${formatCurrency(total.opening_balance)}</td></tr>
      <tr class="totals"><td colspan="4">Income</td><td style="text-align:right">${formatCurrency(total.income_total)}</td></tr>
      <tr class="totals"><td colspan="4">Expense</td><td style="text-align:right">${formatCurrency(total.expense_total)}</td></tr>
      <tr class="totals"><td colspan="4">Closing balance</td><td style="text-align:right">${formatCurrency(total.closing_balance)}</td></tr>
      </table>
      <p>Generated ${formatDate(new Date().toISOString())}. Figures cover the period shown and the entries this reader may see.</p>`);
    if (!ok) toast.error('The browser blocked the print window. Allow pop-ups for this site.');
  }

  return (
    <LiveScreen
      actions={statement.data
        ? <button className="fab" onClick={print} title="Print or save as PDF" type="button">
            <Printer size={20} /> <span className="fab-label">Print</span>
          </button>
        : undefined}
      subtitle="What a farmer earned and spent over a period, and what they are owed at the end of it."
      title="P&L sheet"
    >
      <div className="toolbar-row">
        <label className="field"><span>Farmer</span>
          <Select onChange={(value) => { setFarmerId(value); setPlotId(''); setStat(null); }} options={options}
                  placeholder={options.length ? 'Choose a farmer' : 'No farmer on record yet'} value={farmerId} /></label>
        <label className="field"><span>Plot</span>
          <Select onChange={pickPlot}
                  options={farmerId
                    ? (farmerPlots.data ?? []).map((row) => ({
                      id: row.id, label: namePlot(row),
                    }))
                    : (allPlots.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                  placeholder={farmerId ? 'All plots' : 'Or choose a plot'} value={plotId} /></label>
        <label className="field"><span>From</span>
          <input max={to} onChange={(event) => setFrom(event.target.value)} type="date" value={from} /></label>
        <label className="field"><span>To</span>
          <input min={from} onChange={(event) => setTo(event.target.value)} type="date" value={to} /></label>
      </div>

      {!farmerId && <p className="state-note">Choose a farmer, or one of their plots, to see the statement.</p>}

      {ready && (
        <>
          {/* The balances are the statement; the cards count the lines inside it. Shown once -
              the page used to carry the same figures in a panel, a toolbar and a table. */}
          <div className="statement-balances">
            <div><small>Opening balance</small><strong>{formatCurrencyWhole(statement.data?.opening_balance)}</strong></div>
            <div><small>Income</small><strong className="is-income">{formatCurrencyWhole(statement.data?.income_total)}</strong></div>
            <div><small>Expense</small><strong className="is-expense">{formatCurrencyWhole(statement.data?.expense_total)}</strong></div>
            <div><small>Closing balance</small><strong>{formatCurrencyWhole(statement.data?.closing_balance)}</strong></div>
          </div>

          <StatCards active={stat} onSelect={setStat} rows={inPeriod} stats={STATS}
                     secondary={{ sum: (row: EntryRow) => Number(row.amount), unit: '', money: true }} />

          <QueryState empty="No income or expense in this period." query={entries}>
            <DataGrid columns={columns} exportName="farmer-statement" rows={shown} screenKey="farmer.statement" />
          </QueryState>

          {notCounted.length > 0 && (
            <p className="state-note">
              {notCounted.length === 1 ? 'One entry is' : `${notCounted.length} entries are`} still awaiting
              approval, worth {formatCurrency(notCounted.reduce((sum, row) => sum + Number(row.amount), 0))}, and
              {notCounted.length === 1 ? ' is' : ' are'} not in these totals.
            </p>
          )}

          <p className="muted" style={{ fontSize: '12.5px' }}>
            The opening balance is everything before {formatDate(from)}; the closing balance is that plus this
            period. Entries are written by the harvest, sale or payment they belong to and are not edited here.
          </p>
        </>
      )}
    </LiveScreen>
  );
}
